import React, { useState, useEffect } from 'react'
import {
  Modal,
  message,
  Spin,
  Tabs,
  Flex,
  Tag,
} from '@surecompanies/core_components'
import { copyToClipboard } from 'components/CopyLink/CopyText'
import { ModalContent } from './PaymentDetailModal.styles'
import type { PaymentDetailModalProps } from './PaymentDetailModal.types'
import { EmployeeInformationSection } from './EmployeeInformationSection'
import { BenefitInformationSection } from './BenefitInformationSection'
import { ActivityLogSection } from './ActivityLogSection'
import { TransactionTab } from './TransactionTab'
import { CaseManagementSection } from '../CaseManagement/CaseManagementSection'
import { PaymentManagementSection } from '../VcnPaymentManagement'

import { PaymentStatusTag } from '../PaymentStatusTag'
import { normalizeVcnProvider } from 'modules/virtual-cards/normalizeVcnProvider'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import { getPaymentTypeTagColor } from '../payment-assignment.style'

const TAB_DETAILS = 'details'
const TAB_ACTIVITY = 'activity'
const TAB_TRANSACTIONS = 'transactions'

export default function PaymentDetailModal({
  visible,
  paymentCase,
  loading = false,
  onClose,
  onComplete,
  onStatusUpdated,
  canUpdateCase = true,
  currentPaymentAssignmentRole,
  rolePolicy,
}: PaymentDetailModalProps) {
  const [activeTab, setActiveTab] = useState(TAB_DETAILS)

  // Reset to details tab whenever a new case opens
  useEffect(() => {
    if (visible) setActiveTab(TAB_DETAILS)
  }, [visible, paymentCase?.id])

  const handleCopy = (text: string) => {
    const trimmed = text?.trim()
    if (!trimmed || trimmed === 'N/A') return

    void copyToClipboard(trimmed).then((ok) => {
      if (ok) message.success('Copied to clipboard')
    })
  }

  if (!visible) return null

  // A migrated child (migrateToId is set) is read-only: it was superseded by a
  // newer assignment, so status/payment updates must be disabled.
  const isMigratedChild =
    paymentCase?.migrateToId != null && paymentCase.migrateToId !== ''
  const canUpdate = canUpdateCase && !isMigratedChild

  const wexMerchantLogIdForVcnTransactions =
    paymentCase &&
    normalizeVcnProvider(paymentCase.virtualCardType) === VirtualCardType.WEX &&
    paymentCase.virtualCardId?.trim()
      ? paymentCase.virtualCardId.trim()
      : null

  const binderLabel =
    paymentCase?.needsBinderPayment === true
      ? 'Needs Binder Payment'
      : paymentCase?.isBinderPayment === true
        ? 'Binder Payment'
        : null

  const modalTitle = (
    <Flex align="center" justify="space-between" style={{ paddingRight: 40 }}>
      <span>Payment Detail &amp; Update</span>
      <Flex align="center" gap={10}>
        {binderLabel !== null && (
          <Tag
            style={{
              backgroundColor: '#2d5f9e',
              color: '#fff',
              border: 'none',
              fontWeight: 700,
            }}
          >
            {binderLabel}
          </Tag>
        )}
        {paymentCase?.paymentType && (
          <Tag
            color={getPaymentTypeTagColor(
              String(paymentCase.paymentType ?? '')
            )}
          >
            {paymentCase.paymentType}
          </Tag>
        )}
        {paymentCase?.paymentStatus?.id && (
          <PaymentStatusTag statusId={paymentCase.paymentStatus.id} />
        )}
        {isMigratedChild && (
          <Tag
            style={{
              backgroundColor: '#faad14',
              color: '#fff',
              border: 'none',
              fontWeight: 700,
            }}
          >
            Migrated (Read-only)
          </Tag>
        )}
      </Flex>
    </Flex>
  )

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
    >
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : !paymentCase ? null : (
        <ModalContent>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            destroyInactiveTabPane
            style={{ marginBottom: 0 }}
            items={[
              {
                key: TAB_DETAILS,
                label: 'Details',
                children: (
                  <>
                    <EmployeeInformationSection
                      paymentCase={paymentCase}
                      onCopy={handleCopy}
                    />

                    <BenefitInformationSection
                      paymentCase={paymentCase}
                      onCopy={handleCopy}
                    />

                    <PaymentManagementSection
                      paymentCase={paymentCase}
                      onCopy={handleCopy}
                      canUpdateCase={canUpdate}
                      onComplete={onComplete}
                    />

                    <CaseManagementSection
                      paymentCase={paymentCase}
                      onClose={onClose}
                      onComplete={onComplete}
                      onStatusUpdated={onStatusUpdated}
                      canUpdateCase={canUpdate}
                      currentPaymentAssignmentRole={
                        currentPaymentAssignmentRole
                      }
                      rolePolicy={rolePolicy}
                    />
                  </>
                ),
              },
              {
                key: TAB_ACTIVITY,
                label: 'Activity Log',
                children: (
                  <ActivityLogSection
                    activityLogs={paymentCase.activityLogs ?? []}
                  />
                ),
              },
              {
                key: TAB_TRANSACTIONS,
                label: 'Transaction History',
                children: (
                  <TransactionTab
                    benefitId={paymentCase.benefitId}
                    merchantLogId={wexMerchantLogIdForVcnTransactions}
                  />
                ),
              },
            ]}
          />
        </ModalContent>
      )}
    </Modal>
  )
}
